import { Injectable, inject } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { asapScheduler, of, scheduled } from 'rxjs';
import { catchError, switchMap } from 'rxjs/operators';
import { HomesActions } from './homes.action';

import { ModulesActions } from '../modules/modules.action';
import { HomesService } from './homes.service';

import { SettingsState } from '@library/utils/interfaces/settings-state.interface';
import { Store } from '@ngrx/store';
import { Module, RoomHeatingControllerConfiguration } from '../modules/modules.interface';
import { RoomsActions } from '../rooms/rooms.action';
import { Room } from '../rooms/rooms.interface';
import { UserActions } from '../user/user.action';
import { HeatingSystem, HomeCoachsData, HomeSyncPayload, HomesData, Place } from './homes.interface';

@Injectable()
export class HomesEffects {
  private readonly homesService = inject(HomesService);
  private readonly actions$ = inject(Actions);
  private readonly store = inject<Store<SettingsState>>(Store);

  getHomes$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getHomes),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.getHomes(action.payload).pipe(
          switchMap((homes: HomesData) => [
            HomesActions.hideSpinner(),
            HomesActions.getHomesSuccess({ payload: homes.homes, modules: homes.modules }),
            UserActions.setHomeUser({ user: homes.user }),
          ]),
          catchError(({ error }) => of(HomesActions.getHomesFailure({ payload: error }), HomesActions.hideSpinner())),
        );
      }),
    ),
  );

  getHeatingSystem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getHeatingSystem),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.getHeatingSystem(action.payload.homeId).pipe(
          switchMap((config: HeatingSystem) => [
            HomesActions.hideSpinner(),
            HomesActions.getHeatingSystemSuccess({ payload: config }),
          ]),
          catchError(({ error }) =>
            of(HomesActions.getHeatingSystemFailure({ payload: error }), HomesActions.hideSpinner()),
          ),
        );
      }),
    ),
  );

  getHeatingControllers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getHomeHeatingController),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.getHomeHeatingControllers(action.payload.gatewayId).pipe(
          switchMap((config: RoomHeatingControllerConfiguration) => [
            HomesActions.hideSpinner(),
            HomesActions.getHomeHeatingControllerSuccess({ payload: config }),
          ]),
          catchError(({ error }) =>
            of(HomesActions.getHomeHeatingControllerFailure({ payload: error }), HomesActions.hideSpinner()),
          ),
        );
      }),
    ),
  );

  setHeatingControllersHeatingSystem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setHeatingControllerHeatingSystem),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.setHeatingControllerHeatingSystem(action.payload).pipe(
          switchMap(() => [
            HomesActions.hideSpinner(),
            // Reload the heating system to get the updated HeatingSystem data
            HomesActions.getHeatingSystem({ payload: { homeId: action.payload.home_id } }),
            ModulesActions.getMinHeatingTime({ payload: { device_id: action.payload.device_id } }),
          ]),
          catchError(({ error }) =>
            of(HomesActions.getHeatingSystemFailure({ payload: error }), HomesActions.hideSpinner()),
          ),
        );
      }),
    ),
  );

  setRHCConfiguration$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setRHCConfiguration),
      switchMap((action) => {
        return this.homesService.updateRHCConfiguration(action.payload).pipe(
          switchMap((res) => [HomesActions.setRHCConfigurationSuccess({ payload: res })]),
          catchError(({ error }) => of(HomesActions.setRHCConfigurationFailure({ payload: error }))),
        );
      }),
    ),
  );

  getHomeCoachs$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getHomeCoachs),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.getHomeCoachs(action.payload).pipe(
          switchMap((homeCoachs: HomeCoachsData) => [
            HomesActions.hideSpinner(),
            HomesActions.getHomeCoachsSuccess({ payload: homeCoachs }),
            ModulesActions.getModulesHomeCoachsSuccess({ payload: homeCoachs }),
            RoomsActions.getRoomsHomeCoachsSuccess({ payload: homeCoachs }),
          ]),
          catchError(({ error }) =>
            of(
              HomesActions.getHomeCoachsFailure(error),
              ModulesActions.getModulesHomeCoachsFailure(error),
              RoomsActions.getRoomsHomeCoachsFailure(error),
              HomesActions.hideSpinner(),
            ),
          ),
        );
      }),
    ),
  );

  updateHomePlace$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.updateHomePlace),
      switchMap((action) => {
        return this.homesService.updateHomePlace(action.payload).pipe(
          switchMap((updatedHome) => [
            HomesActions.updateHomePlaceSuccess({
              payload: { updatedHome: updatedHome as Place, home_id: action.payload.home_id ?? '' },
            }),
          ]),
          catchError(({ error }) => of(HomesActions.updateHomePlaceFailure(error))),
        );
      }),
    ),
  );

  getHomesStatus$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getHomeStatus),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.getHomeStatus(action.payload).pipe(
          switchMap(({ home, errors }) => [
            HomesActions.hideSpinner(),
            ModulesActions.setModulesStatusErrors({
              payload: { id: action.payload.home_id ?? '', errors: errors || [] },
            }),
            HomesActions.getHomeStatusSuccess({
              payload: home || { id: action.payload.home_id ?? '', rooms: [], modules: [] },
            }),
          ]),
          catchError(({ error }) => of(HomesActions.getHomeStatusFailure(error), HomesActions.hideSpinner())),
        );
      }),
    ),
  );

  getReadonlyDeviceState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getReadonlyDeviceState),
      switchMap(() => {
        return this.homesService.getReadonlyDeviceState().pipe(
          switchMap(({ modules, errors }) => [
            HomesActions.getReadonlyDeviceStateSuccess({ modules: modules ?? [], errors: errors ?? [] }),
          ]),
          catchError(({ error }) => of(HomesActions.getReadonlyDeviceStateFailure(error))),
        );
      }),
    ),
  );

  getHomesConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getHomeConfig),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.getHomeConfig(action.payload).pipe(
          switchMap(({ home, errors }) => [
            HomesActions.hideSpinner(),
            ModulesActions.setModulesErrors({ payload: { id: action.payload.home_id ?? '', errors: errors || [] } }),
            HomesActions.getHomeConfigSuccess({
              payload: home || { id: action.payload.home_id ?? '', modules: [], rooms: [] },
            }),
          ]),
          catchError((error) => {
            return of(
              HomesActions.getHomeConfigFailure(error),
              HomesActions.hideSpinner(),
              HomesActions.showErrorModal(),
            );
          }),
        );
      }),
    ),
  );

  setHomesConfig$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setHomeGatewayConfig),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.setHomeConfig(action.payload).pipe(
          switchMap(({ errors }) => [
            ModulesActions.setModulesErrors({ payload: { id: action.payload.home_id ?? '', errors: errors || [] } }),
            ModulesActions.updateModulesSetConfig({ payload: action.payload }),
            RoomsActions.updateRoomsSetConfig({ payload: action.payload }),
            HomesActions.hideSpinner(),
            HomesActions.setHomeGatewayConfigSuccess(),
          ]),
          catchError(({ error }) =>
            of(
              HomesActions.setHomeGatewayConfigFailure(error),
              HomesActions.hideSpinner(),
              HomesActions.showErrorModal(),
            ),
          ),
        );
      }),
    ),
  );

  getHomeUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getHomeUsers),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.getHomeUsers(action.payload).pipe(
          switchMap((users) => [
            HomesActions.getHomeUsersSuccess({ payload: { id: action.payload, users } }),
            HomesActions.hideSpinner(),
          ]),
          catchError(({ error }) =>
            of(HomesActions.hideSpinner(), HomesActions.showErrorModal(), HomesActions.getHomeUsersFailure(error)),
          ),
        );
      }),
    ),
  );

  removeUserFromHome$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.removeUserFromHome),
      switchMap((action) => {
        return this.homesService.removeUserAccessToHome(action.payload.homeId, action.payload.userId).pipe(
          switchMap(() => [
            HomesActions.removeUserFromHomeSuccess({
              payload: { homeId: action.payload.homeId, userId: action.payload.userId },
            }),
          ]),
          catchError(({ error }) => of(HomesActions.showErrorModal(), HomesActions.removeUserFromHomeFailure(error))),
        );
      }),
    ),
  );

  updateHomeName$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.updateHomeName),
      switchMap((action) => {
        return this.homesService.updateHome(action.payload).pipe(
          switchMap(() => [HomesActions.updateHomeNameSuccess({ payload: action.payload })]),
          catchError(({ error }) => of(HomesActions.updateHomeNameFailure(error), HomesActions.showErrorModal())),
        );
      }),
    ),
  );

  getAdminAccessCode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getAdminAccessCode),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.generateAdminAccessCode(action.payload).pipe(
          switchMap((res: { admin_access_code: string }) => [
            HomesActions.getAdminAccessCodeSuccess({
              payload: { homeId: action.payload, accessCode: res.admin_access_code },
            }),
            HomesActions.hideSpinner(),
          ]),
          catchError(({ error }) =>
            of(
              HomesActions.hideSpinner(),
              HomesActions.showErrorModal(),
              HomesActions.getAdminAccessCodeFailure(error),
            ),
          ),
        );
      }),
    ),
  );

  setState$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setHomeState),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.setState(action.payload, action.header).pipe(
          switchMap((payload) => {
            if (payload.errors) {
              return [
                HomesActions.setHomeStateError({ payload: { errors: payload.errors } }),
                HomesActions.hideSpinner(),
              ];
            } else {
              const returnPayload = action.payload.home as HomeSyncPayload;
              return [
                HomesActions.setHomeStateSuccess(),
                ModulesActions.updateModulesStatus({ payload: returnPayload }),
                RoomsActions.updateRoomsStatus({ payload: returnPayload }),
                HomesActions.hideSpinner(),
              ];
            }
          }),

          catchError(({ error }) =>
            of(HomesActions.setHomeStateFailure(error), HomesActions.hideSpinner(), HomesActions.showErrorModal()),
          ),
        );
      }),
    ),
  );

  ackLocalResources$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.ackLocalResources),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.ackLocalResources(action.payload, action.header).pipe(
          switchMap((payload) => {
            if (payload.errors) {
              return [HomesActions.ackLocalResourcesFailure({ error: payload.errors }), HomesActions.hideSpinner()];
            } else {
              const returnPayload: HomeSyncPayload = {
                id: action.payload.home_id,
                rooms: action.payload.rooms as Room[],
                modules: action.payload.modules as Module[],
              };
              return [
                HomesActions.ackLocalResourcesSuccess(),
                ModulesActions.updateModulesStatus({ payload: returnPayload }),
                RoomsActions.updateRoomsStatus({ payload: returnPayload }),
                HomesActions.hideSpinner(),
              ];
            }
          }),

          catchError(({ error }) =>
            of(HomesActions.ackLocalResourcesFailure(error), HomesActions.hideSpinner(), HomesActions.showErrorModal()),
          ),
        );
      }),
    ),
  );

  setTemperatureControlMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setTemperatureControlMode),
      switchMap((action) => {
        return this.homesService.setHomeData(action.payload).pipe(
          switchMap(() => [HomesActions.setTemperatureControlSuccess({ payload: action.payload })]),
          catchError(({ error }) => of(HomesActions.setTemperatureControlFailure({ payload: error }))),
        );
      }),
    ),
  );

  getHomeNFA$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.getHomeNFA),
      switchMap((action) => {
        return this.homesService.getHomeNFA(action.payload).pipe(
          switchMap((res) => [HomesActions.getHomeNFASuccess({ payload: res })]),
          catchError(({ error }) => of(HomesActions.getHomeNFAFailure({ payload: error }))),
        );
      }),
    ),
  );

  setHomeType$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setHomeType),
      switchMap((action) => {
        return this.homesService.setHomeData(action.payload).pipe(
          switchMap(() => [HomesActions.setHomeTypeSuccess({ payload: action.payload })]),
          catchError(({ error }) => scheduled(of(HomesActions.setHomeTypeFailure({ payload: error })), asapScheduler)),
        );
      }),
    ),
  );

  setHeatingSystem$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setHomeHeatingSystem),
      switchMap((action) => {
        this.store.dispatch(HomesActions.showSpinner());
        return this.homesService.setHeatingSystem(action.payload).pipe(
          switchMap(() => [
            HomesActions.hideSpinner(),
            HomesActions.setHomeHeatingSystemSuccess({ payload: action.payload }),
          ]),
          catchError(({ error }) =>
            scheduled(
              of(HomesActions.hideSpinner(), HomesActions.setHomeHeatingSystemFailure({ payload: error })),
              asapScheduler,
            ),
          ),
        );
      }),
    ),
  );

  setHomeSize$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setHomeSize),
      switchMap((action) => {
        return this.homesService.setHeatingSystem(action.payload).pipe(
          switchMap(() => [HomesActions.setHomeSizeSuccess({ payload: action.payload })]),
          catchError(({ error }) => scheduled(of(HomesActions.setHomeSizeFailure({ payload: error })), asapScheduler)),
        );
      }),
    ),
  );

  removeDeviceFromHome$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.removeDeviceFromHome),
      switchMap((action) => {
        return this.homesService.removeDeviceFromHome(action.payload).pipe(
          switchMap((res) => [HomesActions.removeDeviceFromHomeSuccess({ payload: res })]),
          catchError(({ error }) => of(HomesActions.removeDeviceFromHomeFailure({ payload: error }))),
        );
      }),
    ),
  );

  setThermMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setThermMode),
      switchMap((action) => {
        return this.homesService.setThermMode(action.homeId, action.therm_mode, action.endtime).pipe(
          switchMap(() => [
            HomesActions.setThermModeSuccess({
              homeId: action.homeId,
              therm_mode: action.therm_mode,
              endtime: action.endtime,
            }),
          ]),
          catchError(({ error }) => of(HomesActions.setThermModeFailure({ error }))),
        );
      }),
    ),
  );

  setThermPoint$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setThermPoint),
      switchMap((action) => {
        return this.homesService.setThermPoint(action.params).pipe(
          switchMap(() => [HomesActions.setThermPointSuccess({ ...action.params })]),
          catchError(({ error }) => of(HomesActions.setThermPointFailure({ error }))),
        );
      }),
    ),
  );

  setHeatingModeMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setThermoregulationHeatingMode),
      switchMap((action) => {
        return this.homesService.setHomeData(action.payload).pipe(
          switchMap(() => [HomesActions.setThermoregulationHeatingModeSuccess({ payload: action.payload })]),
          catchError(({ error }) => of(HomesActions.setThermoregulationHeatingModeFailure({ error }))),
        );
      }),
    ),
  );

  setCoolingMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setThermoregulationCoolingMode),
      switchMap((action) => {
        return this.homesService.setHomeData(action.payload).pipe(
          switchMap(() => [HomesActions.setThermoregulationCoolingModeSuccess({ payload: action.payload })]),
          catchError(({ error }) => of(HomesActions.setThermoregulationCoolingModeFailure({ error }))),
        );
      }),
    ),
  );

  setAutoMode$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.setThermoregulationAutoMode),
      switchMap((action) => {
        return this.homesService.setHomeData(action.payload).pipe(
          switchMap(() => [HomesActions.setThermoregulationAutoModeSuccess({ payload: action.payload })]),
          catchError(({ error }) => of(HomesActions.setThermoregulationAutoModeFailure({ error }))),
        );
      }),
    ),
  );

  loadForecast$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.loadForecast),
      switchMap((action) => {
        return this.homesService.loadForecast(action.payload).pipe(
          switchMap((res) => [HomesActions.loadForecastSuccess({ payload: res })]),
          catchError(({ error }) => of(HomesActions.loadForecastFailure({ error }))),
        );
      }),
    ),
  );

  loadComparisonData$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.loadReportsComparison),
      switchMap((action) => {
        return this.homesService.loadComparisonData(action.payload).pipe(
          switchMap((res) => [
            HomesActions.loadReportsComparisonSuccess({
              payload: { ...res, month: action.payload.month - 1, year: action.payload.year },
            }), // month api starts from 1, moment starts at 0
          ]),
          catchError(({ error }) => of(HomesActions.loadReportsComparisonFailure({ error }))),
        );
      }),
    ),
  );

  removeHomeModules$ = createEffect(() =>
    this.actions$.pipe(
      ofType(HomesActions.removeHome),
      switchMap((action) => [ModulesActions.removeHomeModules({ payload: action.payload })]),
    ),
  );
}
